import { useId } from "react";
import { useAkcjePrzegladu } from "../../hooks/usePrzegladRecenzji.js";
import {
  nazwaEdycjiUwagi,
  nazwaUsunieciaUwagi,
  tytulUwagi,
  type PozycjaUwagi,
  type WpisUwagi,
} from "../../lib/uwagiWypowiedzi.js";
import { IconEdytuj, IconKosz } from "../icons/index.js";
import { Badge } from "../ui/Badge.js";
import { Spinner } from "../ui/Spinner.js";

/**
 * Zapisana uwaga recenzenta: karta pod wypowiedzia z trescia, parametrem, "Edytuj" i "Usun". Kazda uwaga ma wlasne
 * przyciski, wiec kilka uwag pod jedna wypowiedzia nie wymaga zadnych przypadkow szczegolnych. "Usun" otwiera okno
 * potwierdzenia (usuniecie jest nieodwracalne); samo usuwanie idzie w tle, a karta pokazuje "Usuwam...".
 *
 * Przyciski sa zwyklymi <button> z klasa tekstowa (.rec-btn-tekst), nie prymitywem Button: w wierszu transkryptu kazda
 * akcja w ramce wygladalaby jak ciezki formularz. Nazwy dla czytnika mowia, ktorej uwagi dotycza: niosa numer
 * wypowiedzi i poczatek tresci uwagi ("Usun uwage do wypowiedzi 3: ..."), a nazwa grupy, gdy pod wypowiedzia jest ich
 * wiecej, takze miejsce uwagi ("uwaga 2 z 3"). Dzieki temu czytnik ekranu odroznia uwagi, a "Usun" jest nieodwracalne.
 */
export function UwagaZapisana({ wpis, pozycja = null }: { wpis: WpisUwagi; pozycja?: PozycjaUwagi | null }): React.JSX.Element | null {
  const akcje = useAkcjePrzegladu();
  const id = useId();
  const { uwaga, usuwana } = wpis;
  if (!uwaga) return null;

  return (
    <div className="rec-uwaga" role="group" aria-labelledby={`${id}-tytul`} data-klucz={uwaga.klucz} aria-busy={usuwana || undefined}>
      <p className="rec-uwaga__tytul" id={`${id}-tytul`}>
        {tytulUwagi(uwaga.numerWypowiedzi, pozycja)}
      </p>
      <p className="rec-uwaga__tekst">{uwaga.komentarz}</p>
      <div className="rec-uwaga__meta">
        {uwaga.parametr && <Badge>{uwaga.parametr}</Badge>}
        <button
          type="button"
          className="rec-btn-tekst rec-uwaga__edytuj"
          aria-label={nazwaEdycjiUwagi(uwaga.numerWypowiedzi, uwaga.komentarz)}
          aria-disabled={usuwana || undefined}
          onClick={() => {
            if (!usuwana) akcje.edytujUwage(uwaga.klucz);
          }}
        >
          <IconEdytuj size={16} />
          Edytuj
        </button>
        <button
          type="button"
          className="rec-btn-tekst rec-uwaga__usun"
          aria-label={nazwaUsunieciaUwagi(uwaga.numerWypowiedzi, uwaga.komentarz)}
          aria-disabled={usuwana || undefined}
          onClick={() => {
            if (!usuwana) akcje.zadajUsuniecieUwagi(uwaga);
          }}
        >
          <IconKosz size={16} />
          Usuń
        </button>
        {usuwana && (
          <span className="rec-uwaga__usuwa" role="status">
            <Spinner rozmiar="s" />
            Usuwam...
          </span>
        )}
      </div>
    </div>
  );
}
